import { Fragment, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { toast } from 'sonner'
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react'

import { VACATION_PAYMENT_DISCLAIMER } from '@czas/shared'

import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'

import PaymentBadge from '@/features/vacation/payment-badge'
import VacationRequestDetails, {
  ExpandToggle,
  formatSubmittedShort,
  formatWaitingDuration,
} from '@/features/vacation/vacation-request-details'
import { useAdminUsers } from '@/features/admin/use-admin-users'
import {
  useAdminDeleteVacationRequest,
  useAdminVacationRequests,
  useApproveVacationRequest,
  type AdminVacationRequest,
} from '@/features/admin/use-admin-vacation'
import AdminVacationRejectDialog from '@/features/admin/admin-vacation-decision-dialog'
import AdminVacationCreateDialog from '@/features/admin/admin-vacation-create-dialog'
import AdminVacationEditDialog from '@/features/admin/admin-vacation-edit-dialog'
import type { VacationStatus, VacationType } from '@/features/vacation/use-vacation'

const TYPE_LABEL: Record<VacationType, string> = {
  WYPOCZYNKOWY: 'Wypoczynkowy',
  NA_ZADANIE: 'Na żądanie',
  BEZPLATNY: 'Bezpłatny',
  CHOROBOWY: 'Chorobowy (L4)',
  OPIEKA_NAD_DZIECKIEM: 'Opieka nad dzieckiem',
}

const STATUS_LABEL: Record<VacationStatus, string> = {
  PENDING: 'Oczekujący',
  APPROVED: 'Zatwierdzony',
  REJECTED: 'Odrzucony',
  CANCELLED: 'Anulowany',
}

type StatusFilter = VacationStatus | 'ALL'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'PENDING', label: 'Oczekujące' },
  { value: 'APPROVED', label: 'Zatwierdzone' },
  { value: 'REJECTED', label: 'Odrzucone' },
  { value: 'CANCELLED', label: 'Anulowane' },
  { value: 'ALL', label: 'Wszystkie' },
]

function yearOptions(): number[] {
  const current = new Date().getFullYear()
  const out: number[] = []
  for (let y = current - 1; y <= current + 3; y++) out.push(y)
  return out
}

function formatRange(start: string, end: string): string {
  const [sy, sm, sd] = start.split('-').map(Number)
  const [ey, em, ed] = end.split('-').map(Number)
  const sDate = new Date(Date.UTC(sy!, sm! - 1, sd!))
  const eDate = new Date(Date.UTC(ey!, em! - 1, ed!))
  if (start === end) return format(sDate, 'd MMM yyyy', { locale: pl })
  if (sy === ey && sm === em) {
    return `${sd}–${ed} ${format(sDate, 'MMM yyyy', { locale: pl })}`
  }
  if (sy === ey) {
    return `${format(sDate, 'd MMM', { locale: pl })} – ${format(eDate, 'd MMM yyyy', { locale: pl })}`
  }
  return `${format(sDate, 'd MMM yyyy', { locale: pl })} – ${format(eDate, 'd MMM yyyy', { locale: pl })}`
}

function StatusBadge({ status }: { status: VacationStatus }) {
  const map: Record<VacationStatus, { cls: string; Icon: typeof Clock }> = {
    PENDING: { cls: 'bg-warning/10 text-warning border-warning/30', Icon: Clock },
    APPROVED: { cls: 'bg-success/10 text-success border-success/30', Icon: CheckCircle2 },
    REJECTED: { cls: 'bg-danger/10 text-danger border-danger/30', Icon: XCircle },
    CANCELLED: { cls: 'bg-bg-surface text-text-muted border-border-subtle', Icon: Ban },
  }
  const { cls, Icon } = map[status]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 text-xs font-medium',
        cls,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {STATUS_LABEL[status]}
    </span>
  )
}

export default function AdminVacationPage() {
  const currentYear = new Date().getFullYear()
  const [status, setStatus] = useState<StatusFilter>('PENDING')
  const [userId, setUserId] = useState<string>('all')
  const [year, setYear] = useState<number>(currentYear)
  const [createOpen, setCreateOpen] = useState(false)
  const [rejecting, setRejecting] = useState<AdminVacationRequest | null>(null)
  const [editing, setEditing] = useState<AdminVacationRequest | null>(null)
  const [deleting, setDeleting] = useState<AdminVacationRequest | null>(null)
  const [approvingId, setApprovingId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const approve = useApproveVacationRequest()
  const remove = useAdminDeleteVacationRequest()

  async function handleDelete() {
    if (!deleting) return
    try {
      await remove.mutateAsync(deleting.id)
      toast.success(`Wniosek usunięty — ${deleting.user.name}`)
      setDeleting(null)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  // Zatwierdzenie idzie jednym kliknięciem — bez modala i bez komentarza.
  // Odmowa nadal wymaga uzasadnienia (pracownik musi wiedzieć dlaczego).
  async function handleApprove(request: AdminVacationRequest) {
    setApprovingId(request.id)
    try {
      await approve.mutateAsync({ id: request.id })
      toast.success(`Urlop zatwierdzony — ${request.user.name}`)
    } catch (err) {
      toast.error(apiErrorMessage(err))
    } finally {
      setApprovingId(null)
    }
  }

  const usersQuery = useAdminUsers({ active: 'all' })

  const requestsQuery = useAdminVacationRequests({
    status: status === 'ALL' ? undefined : status,
    userId: userId === 'all' ? undefined : userId,
    year,
  })

  const requests = useMemo(
    () => requestsQuery.data?.requests ?? [],
    [requestsQuery.data],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-semibold">Urlopy</h1>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Wpisz urlop dla pracownika
        </Button>
      </div>

      <section className="rounded-card border border-border-subtle bg-bg-elevated p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <FilterSelect
            id="filter-status"
            label="Status"
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            id="filter-user"
            label="Pracownik"
            value={userId}
            onChange={setUserId}
          >
            <option value="all">Wszyscy</option>
            {(usersQuery.data?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            id="filter-year"
            label="Rok"
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
          >
            {yearOptions().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </FilterSelect>
        </div>
      </section>

      {requestsQuery.error && (
        <Alert variant="error">{apiErrorMessage(requestsQuery.error)}</Alert>
      )}

      <div className="rounded-card border border-border-subtle bg-bg-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-surface text-text-muted text-xs uppercase tracking-wider">
              <tr>
                <th className="w-8" aria-label="Szczegóły" />
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Pracownik</th>
                <th className="text-left px-4 py-3 font-medium">Typ</th>
                <th className="text-left px-4 py-3 font-medium">Zakres</th>
                <th className="text-left px-4 py-3 font-medium">Wpłynął</th>
                <th className="text-right px-4 py-3 font-medium w-16">Dni</th>
                <th className="text-right px-4 py-3 font-medium w-56">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {requestsQuery.isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-text-faint">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" aria-hidden />
                    Ładuję wnioski…
                  </td>
                </tr>
              )}

              {!requestsQuery.isLoading && requests.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-text-faint">
                    Brak wniosków o urlop w wybranych filtrach.
                  </td>
                </tr>
              )}

              {requests.map((r) => {
                const expanded = expandedId === r.id
                const toggle = () => setExpandedId(expanded ? null : r.id)
                return (
                  <Fragment key={r.id}>
                    <tr
                      onClick={toggle}
                      className={cn(
                        'border-t border-border-subtle align-top cursor-pointer transition',
                        expanded ? 'bg-bg-surface/60' : 'hover:bg-bg-surface/40',
                      )}
                    >
                      <td className="pl-2">
                        <ExpandToggle
                          expanded={expanded}
                          panelId={`admin-vac-${r.id}`}
                          onToggle={toggle}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-text-primary">{r.user.name}</div>
                        {r.user.position && (
                          <div className="text-xs text-text-muted">
                            {r.user.position.name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-primary">
                        <span className="inline-flex items-center gap-1.5">
                          {TYPE_LABEL[r.type]}
                          <PaymentBadge type={r.type} />
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-primary whitespace-nowrap">
                        {formatRange(r.startDate, r.endDate)}
                        {r.decidedBy && r.status !== 'PENDING' && (
                          <div className="text-xs text-text-faint mt-0.5">
                            Decyzja: {r.decidedBy.name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-text-muted whitespace-nowrap">
                        <span className="tabular-nums">
                          {formatSubmittedShort(r.createdAt)}
                        </span>
                        {r.status === 'PENDING' && (
                          <span className="block text-xs text-text-faint">
                            czeka {formatWaitingDuration(r.createdAt)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">
                        {r.workingDays}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {r.status === 'PENDING' && (
                            <>
                              <Button
                                type="button"
                                variant="secondary"
                                className="px-3 py-1.5 text-xs border-success/40 text-success hover:bg-success/5"
                                loading={approvingId === r.id}
                                disabled={approvingId !== null}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  void handleApprove(r)
                                }}
                              >
                                Zatwierdź
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                className="px-3 py-1.5 text-xs border-danger/40 text-danger hover:bg-danger/5"
                                disabled={approvingId !== null}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setRejecting(r)
                                }}
                              >
                                Odrzuć
                              </Button>
                            </>
                          )}
                          {(r.status === 'PENDING' || r.status === 'APPROVED') && (
                            <button
                              type="button"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-action text-text-muted hover:text-text-primary hover:bg-bg-surface transition"
                              aria-label={`Edytuj wniosek — ${r.user.name}`}
                              title="Edytuj wniosek"
                              onClick={(e) => {
                                e.stopPropagation()
                                setEditing(r)
                              }}
                            >
                              <Pencil className="h-4 w-4" aria-hidden />
                            </button>
                          )}
                          <button
                            type="button"
                            className="inline-flex items-center justify-center h-8 w-8 rounded-action text-text-muted hover:text-danger hover:bg-danger/5 transition"
                            aria-label={`Usuń wniosek — ${r.user.name}`}
                            title="Usuń wniosek"
                            onClick={(e) => {
                              e.stopPropagation()
                              setDeleting(r)
                            }}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr id={`admin-vac-${r.id}`} className="bg-bg-surface/60">
                        <td colSpan={8} className="px-4 pb-4 pt-1">
                          <VacationRequestDetails request={r} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {rejecting && (
        <AdminVacationRejectDialog
          request={rejecting}
          open
          onClose={() => setRejecting(null)}
        />
      )}

      {createOpen && (
        <AdminVacationCreateDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {editing && (
        <AdminVacationEditDialog
          request={editing}
          open
          onClose={() => setEditing(null)}
        />
      )}

      {deleting && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
        >
          <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle
                className="h-5 w-5 text-warning flex-shrink-0 mt-0.5"
                aria-hidden
              />
              <div>
                <h2 className="text-lg font-semibold mb-1">Usunąć wniosek?</h2>
                <p className="text-sm text-text-muted">
                  <span className="font-medium text-text-primary">
                    {deleting.user.name}
                  </span>
                  {' — '}
                  {TYPE_LABEL[deleting.type]}{' '}
                  <span className="text-text-faint">
                    {formatRange(deleting.startDate, deleting.endDate)} ·{' '}
                    {deleting.workingDays} dni
                  </span>
                </p>
                <p className="text-sm text-text-muted mt-2">
                  Operacja jest nieodwracalna. Wpis w audit logu pozostanie.
                  {deleting.status === 'APPROVED' &&
                    (deleting.type === 'WYPOCZYNKOWY' ||
                      deleting.type === 'NA_ZADANIE') &&
                    ' Wykorzystane dni wrócą do puli urlopowej pracownika.'}
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setDeleting(null)}
              >
                Anuluj
              </Button>
              <Button
                type="button"
                onClick={() => void handleDelete()}
                loading={remove.isPending}
                className="bg-danger text-bg-elevated hover:opacity-90"
              >
                Tak, usuń
              </Button>
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-text-faint text-center pt-2">
        {VACATION_PAYMENT_DISCLAIMER}
      </p>
    </div>
  )
}

function FilterSelect({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs text-text-muted uppercase tracking-wider">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-action border border-border-strong bg-bg-elevated px-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
      >
        {children}
      </select>
    </div>
  )
}
